import React, { useState } from 'react';
import { Button, Avatar, CircularProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import '../../ImageWidget.css';

/**
 * Fixed - תבנית תמונה קבועה (Business Component)
 * 
 * @param {string} imageUrl - URL של התמונה
 * @param {function} onSave - callback לשמירת התמונה
 * @param {function} onUploadImage - callback להעלאת תמונה
 * @param {function} onDeleteImage - callback למחיקת תמונה
 * @param {number} FIXED_IMAGE_WIDTH - רוחב התמונה
 * @param {number} FIXED_IMAGE_HEIGHT - גובה התמונה
 */
const Fixed = ({ imageUrl, onSave, onUploadImage, onDeleteImage, FIXED_IMAGE_WIDTH, FIXED_IMAGE_HEIGHT }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftImageUrl, setDraftImageUrl] = useState(imageUrl || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('יש להעלות קובץ תמונה בלבד');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('גודל הקובץ חייב להיות עד 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      if (onUploadImage) {
        const url = await onUploadImage(file);
        setDraftImageUrl(url);
      }
    } catch (err) {
      setError('שגיאה בהעלאת התמונה');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!draftImageUrl) return;
    try {
      if (onDeleteImage) {
        await onDeleteImage(draftImageUrl);
        setDraftImageUrl('');
      }
    } catch (err) {
      setError('שגיאה במחיקת התמונה');
    }
  };

  const handleSave = () => {
    if (onSave) onSave(draftImageUrl);
    setIsEditing(false);
  };

  return (
    <>
      <div className="image-widget">
        {imageUrl ? (
          <img src={imageUrl} alt="Widget" className="image-widget__image" />
        ) : (
          <div className="image-widget__placeholder">אין תמונה</div>
        )}
      </div>

      {isEditing && (
        <>
          <div className="image-widget__modal-overlay" onClick={() => setIsEditing(false)} />
          <div className="image-widget__modal">
            <div className="image-widget__modal-header">
              <h3 className="image-widget__modal-title">עריכת תמונה</h3>
              <button className="image-widget__modal-close" onClick={() => setIsEditing(false)}>×</button>
            </div>

            <div className="image-widget__modal-content">
              {draftImageUrl && (
                <Avatar
                  src={draftImageUrl}
                  variant="square"
                  sx={{ width: 200, height: 200, border: '2px solid #667eea' }}
                />
              )}

              {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={uploading}
                >
                  העלה תמונה
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                </Button>

                {draftImageUrl && !uploading && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    מחק
                  </Button>
                )}
              </div>
            </div>

            <div className="image-widget__modal-footer">
              <Button onClick={() => setIsEditing(false)} variant="outlined">ביטול</Button>
              <Button onClick={handleSave} variant="contained">שמור</Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Fixed;
